import { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { LOGO_URL } from "../data";
import { ArrowLeft, Plus, Copy, Check, Trash2, Users, Link2, Loader2, ShieldCheck } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Admin() {
  const [adminKey, setAdminKey] = useState("");
  const [authed, setAuthed] = useState(false);
  const [partners, setPartners] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", whatsapp: "", code: "" });
  const [copied, setCopied] = useState(null);

  const headers = { "X-Admin-Key": adminKey };
  const origin = window.location.origin;

  const load = async (key) => {
    setError("");
    try {
      const res = await axios.get(`${API}/partners`, { headers: { "X-Admin-Key": key } });
      setPartners(res.data);
      setAuthed(true);
    } catch (e) {
      setError("Falscher Admin-Schlüssel.");
      setAuthed(false);
    }
  };

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    await load(adminKey);
    setLoading(false);
  };

  const addPartner = async (e) => {
    e.preventDefault();
    if (!form.name || !form.whatsapp) return;
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/partners`, form, { headers });
      setForm({ name: "", whatsapp: "", code: "" });
      await load(adminKey);
    } catch (e) {
      setError(e?.response?.data?.detail || "Fehler beim Anlegen.");
    }
    setLoading(false);
  };

  const removePartner = async (code) => {
    setLoading(true);
    try {
      await axios.delete(`${API}/partners/${code}`, { headers });
      await load(adminKey);
    } catch (e) {
      setError("Löschen fehlgeschlagen.");
    }
    setLoading(false);
  };

  const partnerLink = (code) => `${origin}/?ref=${code}`;

  const copyLink = (code) => {
    navigator.clipboard.writeText(partnerLink(code));
    setCopied(code);
    setTimeout(() => setCopied(null), 1500);
  };

  return (
    <div className="min-h-screen bg-[#05070D]">
      <header className="border-b border-white/10 bg-[#05070D]/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-5 py-4 md:px-8">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="TSU-Partners" className="h-10 w-10 rounded-xl object-cover ring-1 ring-white/15" />
            <span className="font-heading text-base font-extrabold text-white">
              TSU<span className="gradient-text">-PARTNERS</span> Admin
            </span>
          </Link>
          <Link to="/" className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-2 text-sm font-semibold text-gray-200 hover:text-white">
            <ArrowLeft className="h-4 w-4" /> Zur Seite
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-5 py-12 md:px-8">
        {!authed ? (
          <form onSubmit={login} className="mx-auto max-w-md rounded-2xl border border-white/10 bg-[#0B111E] p-8" data-testid="admin-login">
            <div className="mb-6 flex items-center gap-3">
              <ShieldCheck className="h-6 w-6 text-[#60A5FA]" />
              <h1 className="font-heading text-xl font-bold text-white">Admin-Bereich</h1>
            </div>
            <label className="mb-1.5 block text-sm font-semibold text-gray-300">Admin-Schlüssel</label>
            <input
              type="password"
              value={adminKey}
              onChange={(e) => setAdminKey(e.target.value)}
              data-testid="admin-key-input"
              placeholder="••••••••"
              className="w-full rounded-xl border border-white/10 bg-[#05070D] px-4 py-3.5 text-white outline-none focus:border-[#3B82F6] focus:ring-2 focus:ring-[#3B82F6]/30"
            />
            {error && <p className="mt-3 text-sm text-red-400" data-testid="admin-error">{error}</p>}
            <button type="submit" disabled={loading} data-testid="admin-login-btn" className="mt-5 flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#1E90FF] to-[#60A5FA] py-3.5 font-bold text-[#04070F] transition-all hover:scale-[1.02] disabled:opacity-70">
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Einloggen"}
            </button>
          </form>
        ) : (
          <div className="space-y-10">
            <div>
              <h1 className="font-heading text-2xl font-extrabold text-white md:text-3xl">Partner verwalten</h1>
              <p className="mt-2 text-sm text-gray-400">Lege Partner an – jeder bekommt seinen eigenen Link mit seiner WhatsApp-Nummer.</p>
            </div>

            {/* Add partner */}
            <form onSubmit={addPartner} className="rounded-2xl border border-white/10 bg-[#0B111E] p-6" data-testid="partner-form">
              <h2 className="mb-4 flex items-center gap-2 font-heading text-lg font-bold text-white"><Plus className="h-5 w-5 text-[#60A5FA]" /> Neuer Partner</h2>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name (z.B. Max)" data-testid="partner-name" className="rounded-xl border border-white/10 bg-[#05070D] px-4 py-3 text-white outline-none focus:border-[#3B82F6]" />
                <input value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} placeholder="WhatsApp (z.B. 49157...)" data-testid="partner-whatsapp" className="rounded-xl border border-white/10 bg-[#05070D] px-4 py-3 text-white outline-none focus:border-[#3B82F6]" />
                <input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="Kürzel (optional)" data-testid="partner-code" className="rounded-xl border border-white/10 bg-[#05070D] px-4 py-3 text-white outline-none focus:border-[#3B82F6]" />
              </div>
              {error && <p className="mt-3 text-sm text-red-400">{error}</p>}
              <button type="submit" disabled={loading} data-testid="partner-add-btn" className="mt-4 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#1E90FF] to-[#60A5FA] px-6 py-3 font-bold text-[#04070F] transition-all hover:scale-[1.02] disabled:opacity-70">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Partner anlegen
              </button>
            </form>

            {/* Partner list */}
            <div>
              <h2 className="mb-4 flex items-center gap-2 font-heading text-lg font-bold text-white"><Users className="h-5 w-5 text-[#60A5FA]" /> Partner ({partners.length})</h2>
              {partners.length === 0 ? (
                <p className="text-sm text-gray-500">Noch keine Partner angelegt.</p>
              ) : (
                <div className="space-y-3" data-testid="partner-list">
                  {partners.map((p) => (
                    <div key={p.code} className="flex flex-col gap-3 rounded-2xl border border-white/10 bg-[#0B111E] p-5 sm:flex-row sm:items-center sm:justify-between">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-white">{p.name}</span>
                          <span className="rounded-full bg-[#3B82F6]/15 px-2 py-0.5 text-xs font-semibold text-[#60A5FA]">{p.lead_count} Leads</span>
                        </div>
                        <div className="mt-1 text-xs text-gray-400">WhatsApp: +{p.whatsapp}</div>
                        <div className="mt-1.5 flex items-center gap-1.5 truncate text-xs text-gray-300">
                          <Link2 className="h-3.5 w-3.5 shrink-0 text-[#60A5FA]" />
                          <span className="truncate">{partnerLink(p.code)}</span>
                        </div>
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        <button onClick={() => copyLink(p.code)} data-testid={`copy-${p.code}`} className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/5 px-4 py-2 text-sm font-semibold text-white hover:bg-white/10">
                          {copied === p.code ? <><Check className="h-4 w-4 text-green-400" /> Kopiert</> : <><Copy className="h-4 w-4" /> Link kopieren</>}
                        </button>
                        <button onClick={() => removePartner(p.code)} className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/15 bg-white/5 text-red-400 hover:bg-red-500/10">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
